import React from 'react'
import { useTranslation } from 'react-i18next'
import { TableCell, TableRow } from '@mui/material'
import { Stack } from '@mui/system'
import classNames from 'classnames'
import { muiTheme } from '@pbt/pbt-ui-components'
import { getPersonString } from '@pbt/pbt-ui-components/src/utils'

import { ConversationTransport, Type } from '~/api/graphql/generated/types'
import SystemMessageLabel from '~/components/dashboard/communications/common/SystemMessageLabel'
import TransportIcon from '~/components/dashboard/communications/TransportIcon'
import { SendGearIcon as SystemMessageIcon } from '~/components/elements/Icons/generated'
import Typography from '~/components/elements/Typography/Typography'
import {
  MESSAGE_TRANSPORT_NAMES,
  MessageStatus,
  MessageWay,
} from '~/constants/communications'
import type { Conversation, ConversationMessage } from '~/types'

import MessageDateCell from './MessageDateCell'
import SubjectCell from './SubjectCell'

import styles from './ConversationRow.module.scss'

const MessageRow = ({
  message,
  conversation,
}: {
  conversation: Conversation
  message: ConversationMessage
}) => {
  const { t } = useTranslation('Common')
  const transport = conversation.transport || ConversationTransport.LogPhoneCall
  const userName = getPersonString(message.creator)
  const from =
    message.way === MessageWay.INBOUND ? conversation.recipient?.name : userName

  const getCurrentRole = () => {
    if (message.way === MessageWay.INBOUND) {
      if (conversation.recipient?.type !== Type.Client) {
        return `, ${t('Common:EXTERNAL_CONTACT')}`
      }
      return `, ${t('Common:PET_PARENT')}`
    }

    return ''
  }

  const SubjectLine = (
    <Stack direction="row" gap={0.5} sx={{ alignItems: 'center' }}>
      {message.isSystemMessage ? (
        <SystemMessageLabel
          color={muiTheme.colors.grayGray2}
          fontSize="Paragraph"
          icon={<SystemMessageIcon sx={{ color: muiTheme.colors.grayGray3 }} />}
        />
      ) : (
        <Typography.Paragraph
          className={classNames({
            [styles.systemMessageSubject]: message.isSystemMessage,
          })}
          sx={{ whiteSpace: 'nowrap', fontWeight: 'fontWeightMedium' }}
        >
          {from}
          {getCurrentRole()}
        </Typography.Paragraph>
      )}
      <Typography.Paragraph
        className={classNames({
          [styles.systemMessageSubject]: message.isSystemMessage,
        })}
      >
        •
      </Typography.Paragraph>
      <TransportIcon
        classes={{
          transportIcon: classNames({
            [styles.transportIcon]: !message.isSystemMessage,
          }),
        }}
        type={conversation.transport}
      />
      <Typography.Paragraph
        className={classNames({
          [styles.systemMessageSubject]: message.isSystemMessage,
        })}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {MESSAGE_TRANSPORT_NAMES[transport]}
      </Typography.Paragraph>
    </Stack>
  )

  return (
    <TableRow
      className={classNames({
        [styles.tableRow]: !message.isSystemMessage,
        [styles.unreadTableRow]: message.isUnread,
        [styles.systemMessageRow]: message.isSystemMessage,
      })}
      key={message.id}
    >
      <TableCell className={classNames(styles.expandCell, styles.noBorder)} />
      <MessageDateCell noBottomBorder lastMessageDate={message.timestamp} />
      <SubjectCell
        noBottomBorder
        conversationId={conversation.id}
        isDraft={message.status === MessageStatus.DRAFT}
        isUnread={message.isUnread}
        message={message.isSystemMessage ? '' : message.body}
        messageId={message.id}
        subject={SubjectLine}
      />
      <TableCell className={styles.noBorder} />
    </TableRow>
  )
}

export default MessageRow
